import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Buildings, Users, GraduationCap, CurrencyInr, ArrowUpRight, DownloadSimple } from "@phosphor-icons/react";
import api, { API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, hint, accent = "slate", testid }) {
  const accents = {
    slate: "bg-slate-100 text-slate-700",
    blue: "bg-blue-100 text-blue-900",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
  };
  return (
    <div
      data-testid={testid}
      className="p-5 border border-slate-200 bg-white rounded-lg flex flex-col gap-3"
    >
      <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accents[accent]}`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
          {label}
        </div>
        <div className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
          {value}
        </div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
    </div>
  );
}

function ActivityRow({ log }) {
  const ts = new Date(log.timestamp);
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className="h-2 w-2 rounded-full bg-blue-900 mt-2 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-900 font-medium truncate">
          <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded mr-2">{log.action}</span>
          {log.user_email || "system"}
        </div>
        {log.metadata && Object.keys(log.metadata).length > 0 && (
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {JSON.stringify(log.metadata)}
          </div>
        )}
      </div>
      <div className="text-[11px] text-slate-400 shrink-0">{ts.toLocaleString()}</div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, actRes] = await Promise.all([
          api.get("/super-admin/dashboard/stats"),
          api.get("/super-admin/dashboard/recent-activity?limit=15"),
        ]);
        setStats(statsRes.data);
        setActivity(actRes.data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="superadmin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            Super Admin
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Hello, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Platform-wide overview across every onboarded school.
          </p>
        </div>
        <Link
          to="/schools"
          data-testid="dashboard-onboard-school-link"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-900 text-white hover:bg-blue-800 rounded-md transition-colors"
        >
          Manage schools
          <ArrowUpRight size={14} weight="bold" />
        </Link>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Buildings}
          label="Total schools"
          value={stats?.total_schools ?? "—"}
          hint={`${stats?.active_schools ?? 0} active · ${stats?.inactive_schools ?? 0} inactive`}
          accent="blue"
          testid="stat-total-schools"
        />
        <StatCard
          icon={Users}
          label="School admins"
          value={stats?.total_school_admins ?? "—"}
          accent="slate"
          testid="stat-total-admins"
        />
        <StatCard
          icon={GraduationCap}
          label="Teachers"
          value={stats?.total_teachers ?? "—"}
          accent="emerald"
          testid="stat-total-teachers"
        />
        <StatCard
          icon={CurrencyInr}
          label="Total collections"
          value={`₹ ${stats?.total_collections ?? 0}`}
          hint="Available after fee module"
          accent="amber"
          testid="stat-total-collections"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-5 border border-slate-200 bg-white rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xl font-medium text-slate-900 font-heading">Recent activity</h3>
            <Link to="/audit" className="text-xs text-blue-900 hover:underline font-medium" data-testid="see-all-audit-link">
              See all logs →
            </Link>
          </div>
          <p className="text-xs text-slate-500 mb-2">Latest platform events</p>
          <div className="divide-y divide-slate-100" data-testid="recent-activity-list">
            {activity.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No activity yet.</div>
            ) : (
              activity.map((log) => <ActivityRow key={log.id} log={log} />)
            )}
          </div>
        </div>

        <div className="p-5 border border-slate-200 bg-white rounded-lg">
          <h3 className="text-xl font-medium text-slate-900 font-heading">Quick actions</h3>
          <p className="text-xs text-slate-500 mt-1">Common super-admin operations</p>
          <div className="mt-4 space-y-2">
            <Link
              to="/schools?action=create"
              data-testid="quick-create-school"
              className="block px-4 py-3 border border-slate-200 hover:bg-slate-50 rounded-md text-sm font-medium text-slate-900 transition-colors"
            >
              + Onboard a new school
            </Link>
            <Link
              to="/schools"
              className="block px-4 py-3 border border-slate-200 hover:bg-slate-50 rounded-md text-sm font-medium text-slate-900 transition-colors"
            >
              View all schools
            </Link>
            <Link
              to="/audit"
              className="block px-4 py-3 border border-slate-200 hover:bg-slate-50 rounded-md text-sm font-medium text-slate-900 transition-colors"
            >
              Browse audit trail
            </Link>
            <BackupDownloads />
          </div>
        </div>
      </div>
    </div>
  );
}

function BackupDownloads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/super-admin/backups");
        setItems(data);
      } catch {
        // silent — endpoint optional
      }
      setLoading(false);
    })();
  }, []);

  const download = async (name) => {
    setDownloading(name);
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API_BASE}/super-admin/backups/${name}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
    setDownloading("");
  };

  if (loading || items.length === 0) return null;

  const fmtBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="backup-downloads">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
        DB backups
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <button
            key={it.name}
            onClick={() => download(it.name)}
            disabled={downloading === it.name}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-slate-200 hover:bg-slate-50 rounded-md text-left transition-colors disabled:opacity-50"
            data-testid={`backup-download-${it.name}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-slate-900 truncate font-mono">{it.name}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {fmtBytes(it.size_bytes)}
                {it.sha256 && ` · sha256 ${it.sha256.slice(0, 12)}…`}
              </div>
            </div>
            <DownloadSimple size={16} className="text-slate-500 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
